import type { Menu, Page, Reference } from '@ps/web-martech-cms-types';
import React, { useContext } from 'react';
import { useLocation } from 'react-router';

import useCMSReference from '../../hooks/useCMSReference';
import CMSMenu from '../CMSMenu/CMSMenu';
import CMSMenuGraphProvider, { CMSMenuGraphContext } from '../CMSMenu/CMSMenuGraphProvider';

import styles from './CMSContextMenu.scss';

interface Props {
  menu: Menu;
  maxDepth?: number;
}

const SubMenuItem: React.FC<{
  item: Reference;
  pathname: string;
  maxDepth: number;
}> = ({ item, pathname, maxDepth }) => {
  const graph = useContext(CMSMenuGraphContext);
  const graphNodes = graph.nodes();
  const menu = useCMSReference<Menu>(item);
  const page = useCMSReference<Page>(menu?.page?.[0]);
  const href = menu?.url || page?.url;

  const expand =
    !!href &&
    !!graphNodes.length &&
    graphNodes.includes(pathname) &&
    graph.shortestPath(graph.nodes()[0], pathname).includes(href);

  return menu ? (
    <li key={item.uid}>
      <CMSMenu
        menu={menu}
        depth={0}
        maxDepth={maxDepth}
        internalItem={styles.navItem}
        activeItem={styles.navItemActive}
        arrowButton={styles.arrowButton}
        collapsedList={styles.collapsed}
        expand={expand}
      />
    </li>
  ) : null;
};

const CMSContextMenu: React.FC<Props> = ({ menu, maxDepth = 3 }) => {
  const { uid, children: submenu } = menu;
  const { pathname } = useLocation();

  return uid && !!submenu.length ? (
    <nav className={styles.nav}>
      <CMSMenuGraphProvider menu={menu}>
        <ul className={styles.navList}>
          {submenu.map((item) => (
            <SubMenuItem key={item.uid} pathname={pathname} item={item} maxDepth={maxDepth} />
          ))}
        </ul>
      </CMSMenuGraphProvider>
    </nav>
  ) : null;
};

export default CMSContextMenu;
